/**
 * The guided-path setup: exam date, first-time or retake, resources, and
 * weekly hours.
 *
 * WHY THESE FOUR AND NOT A LONGER FORM
 * They are the actual inputs Maren's planner consumes, so none of it is
 * friction theatre. exam_date and weekly_hour_target drive the schedule,
 * user_type decides whether Retake Mode applies, and resources decide what
 * the week is built around.
 *
 * WHY IT PERSISTS SEPARATELY FROM THE DIAGNOSTIC
 * Someone can reach checkout, decline, and still deserve to keep what they
 * typed. Losing it would mean spending a high-intent visitor's goodwill and
 * getting nothing for it, which turns the paid lane from upside into a leak.
 * Held locally until there is an account, then written to the profile
 * whether or not they ever pay.
 */

import { authFetch } from "./authFetch";
import { API_BASE } from "./api";

export const SETUP_STORE_KEY = "maren_diagnostic_setup_v1";
export const SETUP_TTL_MS = 24 * 60 * 60 * 1000;

export type UserType = "first_time" | "retaker";

export const HOUR_BANDS = [
  { key: "under5", label: "Under 5", target: 4 },
  { key: "5to10", label: "5 to 10", target: 8 },
  { key: "10to20", label: "10 to 20", target: 15 },
  { key: "over20", label: "Over 20", target: 25 },
] as const;

export type HourBandKey = (typeof HOUR_BANDS)[number]["key"];

export const RESOURCE_OPTIONS = [
  "UWorld",
  "AAMC",
  "Kaplan",
  "Blueprint",
  "Anki",
  "Content review",
  "Something else",
] as const;

export interface DiagnosticSetup {
  /** ISO date (YYYY-MM-DD). */
  examDate: string;
  userType: UserType;
  resources: string[];
  hourBand: HourBandKey;
  savedAt: string;
}

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;

function store(explicit?: Storage): Storage | null {
  if (explicit) return explicit;
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function saveSetup(s: Omit<DiagnosticSetup, "savedAt">, storage?: Storage): void {
  const st = store(storage);
  if (!st) return;
  try {
    st.setItem(SETUP_STORE_KEY, JSON.stringify({ ...s, savedAt: new Date().toISOString() }));
  } catch {
    /* private mode or quota: proceed without persistence */
  }
}

export function loadSetup(storage?: Storage, now: Date = new Date()): DiagnosticSetup | null {
  const st = store(storage);
  if (!st) return null;
  let raw: string | null = null;
  try {
    raw = st.getItem(SETUP_STORE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<DiagnosticSetup>;
    if (!p.examDate || !p.userType || !p.hourBand || !p.savedAt) return null;
    const age = now.getTime() - new Date(p.savedAt).getTime();
    if (!Number.isFinite(age) || age < 0 || age > SETUP_TTL_MS) return null;
    return {
      examDate: p.examDate,
      userType: p.userType,
      resources: Array.isArray(p.resources) ? p.resources : [],
      hourBand: p.hourBand,
      savedAt: p.savedAt,
    };
  } catch {
    return null;
  }
}

export function clearSetup(storage?: Storage): void {
  const st = store(storage);
  if (!st) return;
  try {
    st.removeItem(SETUP_STORE_KEY);
  } catch {
    /* ignore */
  }
}

/** Whole weeks until the exam, floored at 0. Null when no date is set. */
export function weeksUntil(examDate: string | null, now: Date = new Date()): number | null {
  if (!examDate) return null;
  const target = new Date(`${examDate}T00:00:00`).getTime();
  if (!Number.isFinite(target)) return null;
  return Math.max(0, Math.round((target - now.getTime()) / (7 * 24 * 60 * 60 * 1000)));
}

/**
 * The pass that actually covers their window. A recommendation, not a
 * selection: the UI labels this one and still lets them pick any other.
 */
export function recommendedPlan(weeks: number | null): "pass_3mo" | "pass_6mo" | "max" {
  if (weeks === null) return "max";
  if (weeks <= 13) return "pass_3mo";
  return "pass_6mo";
}

export function hourTargetFor(band: HourBandKey): number {
  return HOUR_BANDS.find((b) => b.key === band)?.target ?? 8;
}

export type SetupFlushOutcome =
  | { status: "saved" }
  | { status: "nothing-to-send" }
  | { status: "retry-later" };

let inFlight = false;

/**
 * Writes a held setup onto the account. Called once authenticated, on both
 * paths: the buyer coming back from checkout and the person who declined
 * and took the free lane.
 *
 * Two calls because the fields live in two places: exam date and weekly
 * hours are profile fields, while user_type and resources belong to the
 * onboarding context. Neither failure is surfaced to the user; the setup
 * stays local and retries on the next load.
 */
export async function flushSetup(): Promise<SetupFlushOutcome> {
  if (inFlight) return { status: "retry-later" };
  const setup = loadSetup();
  if (!setup) return { status: "nothing-to-send" };

  inFlight = true;
  try {
    const profileRes = await authFetch(`${API_BASE}/profile`, {
      method: "POST",
      body: JSON.stringify({
        exam_date: setup.examDate,
        // Set from a date picker, so it is a real booked date rather than
        // the planner's "not scheduled yet" estimate.
        exam_date_estimated: false,
        weekly_hour_target: hourTargetFor(setup.hourBand),
      }),
    });

    // Sent separately because the onboarding endpoint requires user_type
    // and owns the resource list. exclude_unset on the backend means this
    // will not blank an existing profile's other context fields.
    const onboardingRes = await authFetch(`${API_BASE}/profile/onboarding`, {
      method: "POST",
      body: JSON.stringify({
        user_type: setup.userType,
        prior_resources: setup.resources,
      }),
    });

    if (profileRes.ok && onboardingRes.ok) {
      clearSetup();
      return { status: "saved" };
    }
    // A 4xx here means this payload will never be accepted; keeping it
    // would retry forever.
    if (
      (profileRes.status >= 400 && profileRes.status < 500) ||
      (onboardingRes.status >= 400 && onboardingRes.status < 500)
    ) {
      clearSetup();
      return { status: "saved" };
    }
    return { status: "retry-later" };
  } catch {
    return { status: "retry-later" };
  } finally {
    inFlight = false;
  }
}
